import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { ProgressBarModule } from '@progress/kendo-angular-progressbar';
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_ICONS } from "@progress/kendo-angular-icons";
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { ChunkUploadInfo, UploadStatus } from '../../models/file-vault.models';

export interface UploadItem {
  file: File;
  progress: number;
  status: UploadStatus;
  error?: string;
  errorCode?: string;
  errorDetails?: string[];
  uploadId?: string;
  chunksCompleted?: number;
  totalChunks?: number;
  /** Base64 data URL for image preview (image mode only) */
  imagePreview?: string;
  /** Slot label when using image mode (e.g. 'Front Image') */
  slotLabel?: string;
}

export type UploadMode = 'standard' | 'image';

export interface ImageSlot {
  label: string;
  item?: UploadItem;
  dragOver: boolean;
}

@Component({
  selector: 'fv-file-upload',
  standalone: true,
  template: `
    <div class="file-upload-container">

      <!-- ============ IMAGE MODE ============ -->
      <ng-container *ngIf="mode === 'image'">
        <div class="image-slots">
          <div *ngFor="let slot of imageSlots; let i = index" class="image-slot">
            <label class="slot-label">{{ slot.label }}</label>

            <!-- Empty slot: drop zone -->
            <div class="image-drop-zone" *ngIf="!slot.item"
                 [class.drag-over]="slot.dragOver"
                 (dragover)="onSlotDragOver($event, i)"
                 (dragleave)="onSlotDragLeave($event, i)"
                 (drop)="onSlotDrop($event, i)"
                 (click)="slotInputs[i]?.click()">
              <input #slotInput type="file" accept="image/*" style="display: none"
                     (change)="onSlotFileSelected($event, i)" />
              <kendo-icon name="image" size="xlarge"></kendo-icon>
              <span class="image-drop-text">Drop image or click to browse</span>
            </div>

            <!-- Filled slot: preview + status -->
            <div class="image-preview-card" *ngIf="slot.item">
              <img [src]="slot.item.imagePreview" [alt]="slot.label" class="image-thumbnail"
                   *ngIf="slot.item.imagePreview" />
              <div class="image-overlay">
                <span class="image-file-name">{{ slot.item.file.name }}</span>
                <span class="image-file-size">{{ formatFileSize(slot.item.file.size) }}</span>
              </div>

              <!-- Progress bar -->
              <div class="image-progress" *ngIf="slot.item.status === 'uploading' || slot.item.status === 'paused'">
                <kendo-progressbar [value]="slot.item.progress" [label]="{ visible: true }"></kendo-progressbar>
              </div>

              <!-- Status badges -->
              <div class="image-status">
                <span class="status-badge completed" *ngIf="slot.item.status === 'completed'">
                  <kendo-icon name="check-circle"></kendo-icon> Uploaded
                </span>
                <span class="status-badge failed" *ngIf="slot.item.status === 'failed'"
                      [title]="slot.item.error || ''">
                  <kendo-icon name="warning-triangle"></kendo-icon> Failed
                </span>
                <span class="status-badge pending" *ngIf="slot.item.status === 'pending'">Ready</span>
              </div>

              <!-- Error detail -->
              <div class="upload-error" *ngIf="slot.item.status === 'failed' && slot.item.error">
                <p class="error-message">{{ slot.item.error }}</p>
              </div>

              <!-- Actions -->
              <div class="image-actions">
                <button kendoButton [look]="'flat'" (click)="removeSlotImage(i)" title="Remove">
                  <kendo-icon name="trash"></kendo-icon>
                </button>
                <button kendoButton [look]="'flat'" (click)="retrySlotUpload(i)"
                        *ngIf="slot.item.status === 'failed'" title="Retry">
                  <kendo-icon name="reset"></kendo-icon>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Upload all button for image mode -->
        <div class="image-upload-actions" *ngIf="hasUploadableItems">
          <button kendoButton [themeColor]="'primary'" (click)="startAll()">
            <kendo-icon name="upload"></kendo-icon> Upload Images
          </button>
        </div>
      </ng-container>

      <!-- ============ STANDARD MODE ============ -->
      <ng-container *ngIf="mode === 'standard'">
        <div class="upload-zone"
             [class.drag-over]="isDragOver"
             (dragover)="onDragOver($event)"
             (dragleave)="onDragLeave($event)"
             (drop)="onDrop($event)"
             (click)="fileInput.click()">
          <input #fileInput type="file" [multiple]="allowMultiple" [accept]="acceptedExtensions"
                 (change)="onFilesSelected($event)" style="display: none" />
          <div class="upload-icon"><kendo-icon name="upload" size="xxxlarge"></kendo-icon></div>
          <p class="upload-text">Drag files here or click to browse</p>
          <p class="upload-hint" *ngIf="acceptedExtensions">Accepted: {{ acceptedExtensions }}</p>
          <p class="upload-hint">Max file size: {{ formatFileSize(maxFileSize) }}</p>
        </div>

        <div class="upload-list" *ngIf="uploadQueue.length > 0">
          <div class="upload-header">
            <span>Upload Queue ({{ uploadQueue.length }})</span>
            <div class="header-actions">
              <button kendoButton (click)="startAll()" [disabled]="!hasUploadableItems">
                <kendo-icon name="play"></kendo-icon> Start All
              </button>
              <button kendoButton (click)="pauseAll()" [disabled]="!hasActiveUploads">
                <kendo-icon name="pause"></kendo-icon> Pause All
              </button>
              <button kendoButton (click)="cancelAll()">
                <kendo-icon name="cancel"></kendo-icon> Clear All
              </button>
            </div>
          </div>

          <div class="upload-items">
            <div *ngFor="let item of uploadQueue; let i = index" class="upload-item" [class]="'status-' + item.status">
              <div class="item-info">
                <span class="file-name">{{ item.file.name }}</span>
                <span class="file-size">{{ formatFileSize(item.file.size) }}</span>
                <span class="status-badge" [class]="item.status">{{ item.status }}</span>
              </div>

              <div class="item-progress" *ngIf="item.status === 'uploading' || item.status === 'paused'">
                <kendo-progressbar [value]="item.progress" [label]="{ visible: true }"></kendo-progressbar>
                <span class="chunk-info" *ngIf="item.totalChunks">
                  Chunk {{ item.chunksCompleted }} / {{ item.totalChunks }}
                </span>
              </div>

              <div class="upload-error" *ngIf="item.status === 'failed'">
                <div class="error-header">
                  <kendo-icon name="warning-triangle"></kendo-icon>
                  <span class="error-title">Upload failed</span>
                  <span class="error-code" *ngIf="item.errorCode">{{ item.errorCode }}</span>
                </div>
                <p class="error-message">{{ item.error || 'An unknown error occurred.' }}</p>
                <ul class="error-details" *ngIf="item.errorDetails?.length">
                  <li *ngFor="let detail of item.errorDetails">{{ detail }}</li>
                </ul>
              </div>

              <div class="item-actions">
                <button kendoButton [look]="'flat'" (click)="startUpload(i)"
                  *ngIf="item.status === 'pending' || item.status === 'paused'" title="Start">
                  <kendo-icon name="play"></kendo-icon>
                </button>
                <button kendoButton [look]="'flat'" (click)="pauseUpload(i)" *ngIf="item.status === 'uploading'" title="Pause">
                  <kendo-icon name="pause"></kendo-icon>
                </button>
                <button kendoButton [look]="'flat'" (click)="cancelUpload(i)" *ngIf="item.status !== 'completed'" title="Cancel">
                  <kendo-icon name="cancel"></kendo-icon>
                </button>
                <button kendoButton [look]="'flat'" (click)="retryUpload(i)" *ngIf="item.status === 'failed'" title="Retry">
                  <kendo-icon name="reset"></kendo-icon>
                </button>
              </div>
            </div>
          </div>
        </div>
      </ng-container>

    </div>
  `,
  styles: [`
    .file-upload-container { display: flex; flex-direction: column; gap: 16px; }
    .upload-zone { border: 2px dashed var(--fv-dropzone-border); border-radius: 8px; padding: 40px 20px; text-align: center; cursor: pointer; transition: all 0.2s ease; background: var(--fv-dropzone-bg); }
    .upload-zone:hover, .upload-zone.drag-over { border-color: var(--fv-dropzone-active-border); background: var(--fv-dropzone-active-bg); }
    .upload-icon { font-size: 48px; margin-bottom: 16px; color: var(--fv-text-muted); }
    .upload-text { font-size: 16px; color: var(--fv-text); margin: 0 0 8px; }
    .upload-hint { font-size: 12px; color: var(--fv-text-muted); margin: 0; }
    .upload-list { border: 1px solid var(--fv-border); border-radius: 8px; overflow: hidden; }
    .upload-header { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: var(--fv-surface-hover); border-bottom: 1px solid var(--fv-border); font-weight: 600; }
    .header-actions { display: flex; gap: 8px; }
    .upload-items { max-height: 400px; overflow-y: auto; }
    .upload-item { padding: 12px 16px; border-bottom: 1px solid var(--fv-border-subtle); }
    .upload-item:last-child { border-bottom: none; }
    .item-info { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
    .file-name { flex: 1; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .file-size { color: var(--fv-text-secondary); font-size: 12px; }
    .status-badge { padding: 2px 8px; border-radius: 12px; font-size: 11px; text-transform: uppercase; }
    .status-badge.pending { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .status-badge.uploading { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .status-badge.paused { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }
    .status-badge.completed { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .status-badge.failed { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .item-progress { margin: 8px 0; }
    .chunk-info { font-size: 11px; color: var(--fv-text-secondary); margin-left: 8px; }
    .upload-error { background: var(--fv-status-error-bg); border: 1px solid var(--fv-status-error-border); border-left: 4px solid var(--fv-status-error-fg); border-radius: 6px; padding: 10px 12px; margin-top: 6px; }
    .error-header { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
    .error-header kendo-icon { color: var(--fv-status-error-fg); font-size: 16px; }
    .error-title { font-weight: 600; font-size: 13px; color: var(--fv-status-error-fg); }
    .error-code { font-size: 11px; font-family: monospace; background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); padding: 1px 6px; border-radius: 4px; margin-left: auto; }
    .error-message { font-size: 13px; color: var(--fv-status-error-fg); margin: 0 0 4px; line-height: 1.4; }
    .error-details { margin: 4px 0 0 16px; padding: 0; font-size: 12px; color: var(--fv-status-error-fg); list-style: disc; }
    .error-details li { margin-bottom: 2px; }
    .item-actions { display: flex; gap: 4px; justify-content: flex-end; }

    /* ---- Image Mode ---- */
    .image-slots { display: flex; gap: 16px; flex-wrap: wrap; }
    .image-slot { flex: 1 1 240px; max-width: 320px; display: flex; flex-direction: column; gap: 6px; }
    .slot-label { font-weight: 600; font-size: 13px; color: var(--fv-text); }
    .image-drop-zone { border: 2px dashed var(--fv-dropzone-border); border-radius: 8px; padding: 32px 16px; text-align: center; cursor: pointer; transition: all 0.2s ease; background: var(--fv-dropzone-bg); display: flex; flex-direction: column; align-items: center; gap: 8px; min-height: 180px; justify-content: center; }
    .image-drop-zone:hover, .image-drop-zone.drag-over { border-color: var(--fv-dropzone-active-border); background: var(--fv-dropzone-active-bg); }
    .image-drop-text { font-size: 12px; color: var(--fv-text-muted); }
    .image-preview-card { position: relative; border: 1px solid var(--fv-border); border-radius: 8px; overflow: hidden; background: var(--fv-surface); }
    .image-thumbnail { width: 100%; height: 200px; object-fit: contain; background: var(--fv-surface-alt); display: block; }
    .image-overlay { padding: 8px 10px; background: var(--fv-surface-alt); border-top: 1px solid var(--fv-border-subtle); }
    .image-file-name { display: block; font-size: 12px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .image-file-size { font-size: 11px; color: var(--fv-text-muted); }
    .image-progress { padding: 6px 10px; }
    .image-status { padding: 0 10px 6px; }
    .image-status .status-badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 12px; font-size: 11px; text-transform: uppercase; }
    .image-status .status-badge.completed { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .image-status .status-badge.failed { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .image-status .status-badge.pending { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .image-actions { display: flex; gap: 4px; padding: 4px 10px 8px; justify-content: flex-end; }
    .image-upload-actions { display: flex; justify-content: flex-end; }
  `],
  imports: [CommonModule, ButtonsModule, ProgressBarModule, IconsModule, KENDO_ICONS]
})
export class FileUploadComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  @ViewChildren('slotInput') slotInputRefs!: QueryList<ElementRef<HTMLInputElement>>;

  // ── Mode ──
  @Input() mode: UploadMode = 'standard';
  @Input() maxFiles?: number;
  @Input() imageSlotLabels: string[] = ['Image 1', 'Image 2'];

  // ── PhotoBit image mode ──
  /** Material number used to rename images: {materialNumber}_top.png, {materialNumber}_side.png */
  @Input() materialNumber?: string;
  /** Sub-PSL name sent to backend to resolve fileshare path via FileTypeDirectory (e.g. 'FixedCutter') */
  @Input() subPslName?: string;
  /** Suffix tags for each image slot, used in file rename: {materialNumber}_{tag}.{ext} */
  @Input() imageSlotTags: string[] = ['top', 'side'];

  @Input() targetPath = '';
  @Input() targetId?: number;
  @Input() applicationTypeId?: number;
  @Input() applicationTypeName?: string;

  private applicationTypeResolved = false;
  @Input() autoCreateDocument = false;
  @Input() documentNumber?: string;
  @Input() documentTitle?: string;
  @Input() documentType?: string;
  @Input() uploadMetadata?: Record<string, string>;
  @Input() allowMultiple = true;
  @Input() acceptedExtensions = '';
  @Input() maxFileSize = 500 * 1024 * 1024;
  @Input() chunkSize = 5 * 1024 * 1024;

  @Output() uploadStarted = new EventEmitter<UploadItem>();
  @Output() uploadProgress = new EventEmitter<UploadItem>();
  @Output() uploadCompleted = new EventEmitter<UploadItem>();
  @Output() uploadFailed = new EventEmitter<UploadItem>();
  @Output() allUploadsCompleted = new EventEmitter<void>();

  uploadQueue: UploadItem[] = [];
  isDragOver = false;

  // ── Image mode state ──
  imageSlots: ImageSlot[] = [];

  /** Convenience accessor for template (click)="slotInputs[i]?.click()" */
  get slotInputs(): HTMLInputElement[] {
    return this.slotInputRefs?.toArray().map(r => r.nativeElement) ?? [];
  }

  ngOnInit(): void {
    this.resolveApplicationTypeName();
    this.initImageSlots();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applicationTypeName'] && !changes['applicationTypeName'].firstChange) {
      this.resolveApplicationTypeName();
    }
    if (changes['imageSlotLabels'] && !changes['imageSlotLabels'].firstChange) {
      this.initImageSlots();
    }
  }

  private resolveApplicationTypeName(): void {
    if (this.applicationTypeName && !this.applicationTypeId) {
      this.applicationTypeResolved = false;
      this.api.getApplicationTypeByName(this.applicationTypeName).subscribe({
        next: (appType) => {
          this.applicationTypeId = appType.id;
          this.applicationTypeResolved = true;
        },
        error: (err) => {
          console.error(`Failed to resolve ApplicationType name '${this.applicationTypeName}':`, err);
          this.applicationTypeResolved = true;
        }
      });
    } else {
      this.applicationTypeResolved = true;
    }
  }

  get hasUploadableItems(): boolean { return this.uploadQueue.some(i => i.status === UploadStatus.Pending || i.status === UploadStatus.Paused); }
  get hasActiveUploads(): boolean { return this.uploadQueue.some(i => i.status === UploadStatus.Uploading); }

  onDragOver(event: DragEvent): void { event.preventDefault(); event.stopPropagation(); this.isDragOver = true; }
  onDragLeave(event: DragEvent): void { event.preventDefault(); event.stopPropagation(); this.isDragOver = false; }
  onDrop(event: DragEvent): void {
    event.preventDefault(); event.stopPropagation(); this.isDragOver = false;
    if (event.dataTransfer?.files) { this.addFiles(Array.from(event.dataTransfer.files)); }
  }
  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) { this.addFiles(Array.from(input.files)); input.value = ''; }
  }

  addFiles(files: File[]): void {
    const validFiles = files.filter(file => {
      if (file.size > this.maxFileSize) { console.warn('File too large: ' + file.name); return false; }
      if (this.acceptedExtensions) {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!this.acceptedExtensions.includes(ext)) { console.warn('File type not accepted: ' + file.name); return false; }
      }
      return true;
    });
    const newItems: UploadItem[] = validFiles.map(file => ({
      file, progress: 0, status: UploadStatus.Pending, totalChunks: Math.ceil(file.size / this.chunkSize)
    }));
    this.uploadQueue.push(...newItems);
  }

  async startUpload(index: number): Promise<void> {
    const item = this.uploadQueue[index];
    if (!item || (item.status !== UploadStatus.Pending && item.status !== UploadStatus.Paused)) return;

    item.status = UploadStatus.Uploading;
    this.uploadStarted.emit(item);

    try {
      this.api.uploadFileChunked(item.file, {
        chunkSize: this.chunkSize,
        targetId: this.targetId,
        applicationType: this.applicationTypeId,
        metadata: this.buildUploadMetadata(item.file),
        subPslName: this.subPslName || undefined
      }).subscribe({
        next: (info: ChunkUploadInfo) => {
          item.progress = info.progress;
          item.chunksCompleted = info.uploadedChunks;
          item.status = info.status;
          if (info.status === UploadStatus.Failed) {
            item.error = info.error;
            item.errorCode = info.errorCode;
            item.errorDetails = info.errorDetails;
            this.uploadFailed.emit(item);
          } else {
            this.uploadProgress.emit(item);
          }
          if (info.status === UploadStatus.Completed) {
            this.uploadCompleted.emit(item);
            this.checkAllCompleted();
          }
        },
        error: (err: Error) => {
          item.status = UploadStatus.Failed;
          item.error = err.message || 'Upload failed';
          this.uploadFailed.emit(item);
        }
      });
    } catch (error: unknown) {
      item.status = UploadStatus.Failed;
      item.error = error instanceof Error ? error.message : 'Upload failed';
      this.uploadFailed.emit(item);
    }
  }

  pauseUpload(index: number): void {
    const item = this.uploadQueue[index];
    if (item && item.status === UploadStatus.Uploading && item.uploadId) {
      this.api.pauseUpload(item.uploadId);
      item.status = UploadStatus.Paused;
    }
  }

  cancelUpload(index: number): void {
    const item = this.uploadQueue[index];
    if (item) {
      if (item.uploadId) { this.api.cancelUpload(item.uploadId); }
      this.uploadQueue.splice(index, 1);
    }
  }

  retryUpload(index: number): void {
    const item = this.uploadQueue[index];
    if (item && item.status === UploadStatus.Failed) {
      item.status = UploadStatus.Pending; item.progress = 0; item.error = undefined;
      this.startUpload(index);
    }
  }

  startAll(): void { this.uploadQueue.forEach((_, i) => { if (this.uploadQueue[i].status === UploadStatus.Pending || this.uploadQueue[i].status === UploadStatus.Paused) { this.startUpload(i); } }); }
  pauseAll(): void { this.uploadQueue.forEach((_, i) => { if (this.uploadQueue[i].status === UploadStatus.Uploading) { this.pauseUpload(i); } }); }
  cancelAll(): void { this.uploadQueue = []; }

  private checkAllCompleted(): void {
    const allDone = this.uploadQueue.every(i => i.status === UploadStatus.Completed || i.status === UploadStatus.Failed || i.status === UploadStatus.Cancelled);
    if (allDone && this.uploadQueue.length > 0) { this.allUploadsCompleted.emit(); }
  }

  formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0; let size = bytes;
    while (size >= 1024 && unitIndex < units.length - 1) { size /= 1024; unitIndex++; }
    return size.toFixed(unitIndex > 0 ? 1 : 0) + ' ' + units[unitIndex];
  }

  // ═══════════════════════════════════════════════
  //  IMAGE MODE
  // ═══════════════════════════════════════════════

  private initImageSlots(): void {
    if (this.mode === 'image') {
      this.imageSlots = this.imageSlotLabels.map(label => ({ label, dragOver: false }));
      // Auto-configure image-friendly defaults
      if (!this.acceptedExtensions) {
        this.acceptedExtensions = '.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff';
      }
    }
  }

  onSlotDragOver(event: DragEvent, slotIndex: number): void {
    event.preventDefault(); event.stopPropagation();
    this.imageSlots[slotIndex].dragOver = true;
  }

  onSlotDragLeave(event: DragEvent, slotIndex: number): void {
    event.preventDefault(); event.stopPropagation();
    this.imageSlots[slotIndex].dragOver = false;
  }

  onSlotDrop(event: DragEvent, slotIndex: number): void {
    event.preventDefault(); event.stopPropagation();
    this.imageSlots[slotIndex].dragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) { this.assignFileToSlot(file, slotIndex); }
  }

  onSlotFileSelected(event: Event, slotIndex: number): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) { this.assignFileToSlot(file, slotIndex); }
    input.value = '';
  }

  private assignFileToSlot(file: File, slotIndex: number): void {
    if (!file.type.startsWith('image/')) {
      console.warn('Only image files are accepted in image mode:', file.name);
      return;
    }
    if (file.size > this.maxFileSize) {
      console.warn('File too large:', file.name);
      return;
    }

    // Rename file to {materialNumber}_{tag}.{ext} if materialNumber and tags are provided
    let uploadFile = file;
    if (this.materialNumber && this.imageSlotTags[slotIndex]) {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const newName = `${this.materialNumber}_${this.imageSlotTags[slotIndex]}.${ext}`;
      uploadFile = new File([file], newName, { type: file.type });
    }

    const slot = this.imageSlots[slotIndex];
    const item: UploadItem = {
      file: uploadFile,
      progress: 0,
      status: UploadStatus.Pending,
      totalChunks: Math.ceil(uploadFile.size / this.chunkSize),
      slotLabel: slot.label
    };
    slot.item = item;

    // Also keep in uploadQueue so startAll / hasUploadableItems work
    this.uploadQueue = this.imageSlots.filter(s => !!s.item).map(s => s.item!);

    // Generate image preview
    this.generatePreview(file, slotIndex);
  }

  private generatePreview(file: File, slotIndex: number): void {
    const reader = new FileReader();
    reader.onload = () => {
      const slot = this.imageSlots[slotIndex];
      if (slot.item) { slot.item.imagePreview = reader.result as string; }
    };
    reader.readAsDataURL(file);
  }

  removeSlotImage(slotIndex: number): void {
    const slot = this.imageSlots[slotIndex];
    if (slot.item?.uploadId) { this.api.cancelUpload(slot.item.uploadId); }
    slot.item = undefined;
    this.uploadQueue = this.imageSlots.filter(s => !!s.item).map(s => s.item!);
  }

  retrySlotUpload(slotIndex: number): void {
    const slot = this.imageSlots[slotIndex];
    if (slot.item && slot.item.status === UploadStatus.Failed) {
      slot.item.status = UploadStatus.Pending;
      slot.item.progress = 0;
      slot.item.error = undefined;
      slot.item.errorCode = undefined;
      slot.item.errorDetails = undefined;
      // Find index in uploadQueue and start it
      const queueIndex = this.uploadQueue.indexOf(slot.item);
      if (queueIndex >= 0) { this.startUpload(queueIndex); }
    }
  }

  private buildUploadMetadata(file: File): Record<string, string> | undefined {
    const metadata: Record<string, string> = { ...(this.uploadMetadata ?? {}) };

    if (this.autoCreateDocument) {
      metadata['autoCreateDocument'] = 'true';
      if (this.documentNumber) {
        metadata['documentNumber'] = this.documentNumber;
      } else {
        metadata['documentNumber'] = file.name.split('.').slice(0, -1).join('.') || file.name;
      }
      if (this.documentTitle) {
        metadata['documentTitle'] = this.documentTitle;
      }
      if (this.documentType) {
        metadata['documentType'] = this.documentType;
      }
    }

    return Object.keys(metadata).length > 0 ? metadata : undefined;
  }
}
